import { formatDate } from "app/main/utils/dateUtils";
import Typography from "@material-ui/core/Typography";
import Icon from "@material-ui/core/Icon";
import moment from "moment";

// Per-trip latest broker communication — based on internalTripsList tracking columns,
// sourced from the broker-comms route in groupByTrip mode (one row per trip + latest comment).
const brokerCommsList = {
	urlGET: "api/profile/carriers/:carrierId/broker-comms",
	idKey: "_id",
	listenEvent: ["profileRevision", "commentRevision"],
	segmentKey: "BROKER_COMMS",
	content: {
		table: {
			variant: "skinny",
			multiline: true,
			orderBy: [],
		},
		pagination: {
			enabled: true,
			resetScroll: true,
		},
		order: {
			defaut: {
				key: "updatedAt",
				dir: "desc",
			},
		},
		defaults: {
			initial: {
				filters: {
					tripStatus: "__NOT_SELECTED__",
				},
			},
			items: [
				{
					label: "All",
					filters: { tripStatus: "__NOT_SELECTED__" },
				},
				{
					label: "Active",
					filters: { tripStatus: "ACTIVE" },
				},
				{
					label: "Delivered",
					filters: { tripStatus: "DELIVERED" },
				},
			],
		},
		filters: {
			search: true,
			searchDescription: "Search by Load ID, Broker or comment text",
			items: [
				{
					key: "tripStatus",
					type: "select",
					label: "Trip Status",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "ACTIVE",
							label: "Active",
						},
						{
							value: "DELIVERED",
							label: "Delivered",
						},
					],
				},
				{
					key: "botOnly",
					type: "select",
					label: "Source",
					defaultValue: "__NOT_SELECTED__",
					options: [
						{
							value: "__NOT_SELECTED__",
							label: "All",
						},
						{
							value: "true",
							label: "Bot Only",
						},
					],
				},
			],
		},
		items: [
			{
				key: "trip__view",
				type: "trips__view",
				label: "Trip",
				table: { width: 150, minWidth: 130 },
				builder: (item) => ({
					value: item.tripId,
					label: item.load_id ? `# ${item.load_id}` : String(item.tripId),
					metadata: {
						status: item.status,
						broker: item.broker,
					},
				}),
				viewConfig: {
					readOnly: true,
					showStatus: true,
					showBroker: true,
				},
			},
			{
				key: "truck__view",
				type: "truck__view",
				label: "Truck",
				table: { width: 150, minWidth: 130 },
				viewConfig: {
					readOnly: true,
					showTeam: true,
				},
			},
			{
				key: "pickup",
				type: "component",
				label: "Pickup",
				table: { width: 200, minWidth: 140 },
				builder: (item) => (
					<div className="flex flex-col">
						<Typography className="break-words whitespace-normal tracking-wide leading-4 mb-2 text-12 ml:text-13">
							{item.pickup_date ? formatDate(item.pickup_date) : "-"}
						</Typography>
						<Typography className="break-words whitespace-normal tracking-wide text-grey-600 text-11 ml:text-12 -mt-3">
							{item.pickup_address || "-"}
						</Typography>
					</div>
				),
			},
			{
				key: "delivery",
				type: "component",
				label: "Delivery",
				table: { width: 200, minWidth: 140 },
				builder: (item) => (
					<div className="flex flex-col">
						<Typography className="break-words whitespace-normal tracking-wide leading-4 mb-2 text-12 ml:text-13">
							{item.delivery_date ? formatDate(item.delivery_date) : "-"}
						</Typography>
						<Typography className="break-words whitespace-normal tracking-wide text-grey-600 text-11 ml:text-12 -mt-3">
							{item.delivery_address || "-"}
						</Typography>
					</div>
				),
			},
			{
				key: "tracking",
				type: "component",
				label: "Tracking",
				table: { width: 200, minWidth: 150 },
				builder: (item) => {
					const lastUpdate = item.current_location_time || item.updatedAt;
					const outdated = item.last_location_expire && moment(item.last_location_expire).isBefore(moment());
					return (
						<div className="flex flex-col">
							<div className="flex flex-row items-center">
								<Icon className={`text-16 mr-4 ${outdated ? "text-orange" : "text-green"}`}>location_on</Icon>
								<Typography className="text-12">{item.current_location || "Pending..."}</Typography>
							</div>
							<Typography className="text-11 text-grey-600">
								{lastUpdate ? moment(lastUpdate).fromNow() : "-"}
							</Typography>
						</div>
					);
				},
			},
			{
				key: "latestComment",
				type: "component",
				label: "Latest Broker Comment",
				table: { width: 480, minWidth: 360 },
				builder: (item) => {
					const comment = item.latestComment;
					if (!comment) {
						return <span className="text-11 text-grey-500">-</span>;
					}
					return (
						<div className="flex flex-col max-w-480">
							<Typography className="text-12 whitespace-normal break-words">{comment.content || "-"}</Typography>
							{comment.extraContent ? (
								<Typography className="text-11 text-grey-600 whitespace-normal break-words">
									{comment.extraContent}
								</Typography>
							) : null}
							<div className="flex flex-row items-center mt-2">
								{comment.isBot && (
									<Icon className="text-14 text-blue mr-2" title="Axle Bot">
										smart_toy
									</Icon>
								)}
								<Typography className="text-11 text-grey-600">
									{comment.creatorUser__view?.label || (comment.isBot ? "Axle Bot" : "-")}
									{comment.createdAt ? ` · ${moment(comment.createdAt).fromNow()}` : ""}
								</Typography>
							</div>
							{item.commentCount > 1 ? (
								<Typography className="text-10 text-grey-500">{item.commentCount} comments</Typography>
							) : null}
						</div>
					);
				},
			},
		],
	},
};

export default brokerCommsList;
